/**
 * Core Tire Calculator Engine
 * Precise calculations for off-road tire comparisons
 * Handles speedometer error, gear ratio changes, RPM changes
 */

import { calculateCircumference, calculateRevolutionsPerMile } from './tireParser.js';
import { calculateRotationalImpact } from './rotationalPhysics.js';
import { getGuidanceFromComparison } from './regearingGuidance.js';
import { calculateClearanceProbability, getVehicleSuspensionType } from './clearanceProbability.js';

/**
 * Calculate comprehensive tire comparison
 * @param {Object} currentTire - Current tire dimensions
 * @param {Object} newTire - New tire dimensions
 * @param {Object} drivetrain - Optional drivetrain specs
 * @param {Object} tireSpecs - Optional tire weight and load rating specs
 * @param {string} intendedUse - Intended use case for context-aware recommendations
 * @returns {Object} Complete comparison data
 */
export function calculateTireComparison(currentTire, newTire, drivetrain = {}, tireSpecs = {}, intendedUse = 'weekend_trail') {
  // Basic tire metrics
  const current = calculateTireMetrics(currentTire);
  const newCalc = calculateTireMetrics(newTire);

  // Differences
  const differences = calculateDifferences(current, newCalc);

  // Speedometer error
  const speedometerError = calculateSpeedometerError(current, newCalc);

  // Drivetrain impact (if gear ratios provided)
  const drivetrainImpact = drivetrain.axleGearRatio
    ? calculateDrivetrainImpact(current, newCalc, drivetrain)
    : null;

  // Clearance and fitment
  const clearance = calculateClearanceImpact(differences, newCalc, drivetrain);

  // Weight and load analysis
  // Use provided weights or estimate based on tire size
  const currentWeight = tireSpecs.currentTireWeight
    ? parseFloat(tireSpecs.currentTireWeight)
    : estimateTireWeight(current);
  const newWeight = tireSpecs.newTireWeight
    ? parseFloat(tireSpecs.newTireWeight)
    : estimateTireWeight(newCalc);

  const weightAnalysis = calculateWeightImpact({
    currentTireWeight: currentWeight,
    newTireWeight: newWeight
  }, !tireSpecs.currentTireWeight || !tireSpecs.newTireWeight, intendedUse); // isEstimate flag + intendedUse

  const loadCapacityAnalysis = (tireSpecs.currentTireLoadIndex && tireSpecs.newTireLoadIndex)
    ? calculateLoadCapacity(tireSpecs, intendedUse)
    : null;

  // Rotational physics analysis (Part 1: Engineering Expansion)
  // Pass the same weights used in weightAnalysis to ensure consistency
  const isWeightEstimated = !tireSpecs.currentTireWeight || !tireSpecs.newTireWeight;
  const rotationalPhysics = calculateRotationalImpact(
    {
      size: currentTire.originalSize || formatDisplay(currentTire),
      diameter: current.diameter,
      weight: currentWeight,
      isEstimated: !tireSpecs.currentTireWeight
    },
    {
      size: newTire.originalSize || formatDisplay(newTire),
      diameter: newCalc.diameter,
      weight: newWeight,
      isEstimated: !tireSpecs.newTireWeight
    }
  );

  // Build comparison object for guidance
  const comparisonForGuidance = {
    differences,
    drivetrainImpact,
    rotationalPhysics
  };

  // Regearing guidance (Real-world advice based on forum data)
  // Requires drivetrain data to provide guidance
  const regearingGuidance = drivetrainImpact
    ? getGuidanceFromComparison(
        comparisonForGuidance,
        intendedUse,
        drivetrain.vehicleType || 'unknown'
      )
    : null;

  return {
    current,
    new: newCalc,
    differences,
    speedometerError,
    drivetrainImpact,
    clearance,
    weightAnalysis,
    loadCapacityAnalysis,
    rotationalPhysics,
    regearingGuidance,
    timestamp: new Date().toISOString()
  };
}

/**
 * Calculate all metrics for a single tire
 */
function calculateTireMetrics(tire) {
  const circumference = calculateCircumference(tire.diameter);
  const revolutionsPerMile = calculateRevolutionsPerMile(circumference);

  return {
    // Source data
    format: tire.format,
    width: tire.width,
    widthInches: tire.width / 25.4,
    aspectRatio: tire.aspectRatio,
    wheelDiameter: tire.wheelDiameter,
    isLT: tire.isLT,

    // Calculated dimensions
    sidewallHeight: tire.sidewallHeight,
    sidewallInches: tire.sidewallInches,
    diameter: tire.diameter,
    diameterMm: tire.diameterMm,

    // Rotational characteristics
    circumference: circumference,
    circumferenceMm: circumference * 25.4,
    revolutionsPerMile: revolutionsPerMile,

    // Display
    formatted: formatDisplay(tire)
  };
}

/**
 * Calculate differences between tires
 */
function calculateDifferences(current, newTire) {
  const diameterDiff = newTire.diameter - current.diameter;
  const diameterPct = (diameterDiff / current.diameter) * 100;

  const widthDiff = newTire.widthInches - current.widthInches;
  const widthPct = (widthDiff / current.widthInches) * 100;

  const sidewallDiff = newTire.sidewallInches - current.sidewallInches;
  const sidewallPct = (sidewallDiff / current.sidewallInches) * 100;

  const circumferenceDiff = newTire.circumference - current.circumference;
  const circumferencePct = (circumferenceDiff / current.circumference) * 100;

  // Ground clearance gain (half of diameter increase)
  const groundClearanceGain = diameterDiff / 2;

  return {
    diameter: {
      absolute: diameterDiff,
      percentage: diameterPct,
      inches: diameterDiff,
      mm: diameterDiff * 25.4
    },
    width: {
      absolute: widthDiff,
      percentage: widthPct,
      inches: widthDiff,
      mm: widthDiff * 25.4
    },
    sidewall: {
      absolute: sidewallDiff,
      percentage: sidewallPct,
      inches: sidewallDiff,
      mm: sidewallDiff * 25.4
    },
    circumference: {
      absolute: circumferenceDiff,
      percentage: circumferencePct,
      inches: circumferenceDiff,
      mm: circumferenceDiff * 25.4
    },
    groundClearance: {
      gain: groundClearanceGain,
      inches: groundClearanceGain,
      mm: groundClearanceGain * 25.4
    },
    revolutionsPerMile: {
      absolute: newTire.revolutionsPerMile - current.revolutionsPerMile,
      percentage: ((newTire.revolutionsPerMile - current.revolutionsPerMile) / current.revolutionsPerMile) * 100
    }
  };
}

/**
 * Calculate speedometer error at various speeds
 */
function calculateSpeedometerError(current, newTire) {
  // Ratio of diameters determines speed error
  const ratio = newTire.diameter / current.diameter;

  const testSpeeds = [30, 45, 60, 75];
  const errors = {};

  testSpeeds.forEach(speed => {
    const actualSpeed = speed * ratio;
    const error = actualSpeed - speed;
    const errorPct = (error / speed) * 100;

    errors[`at${speed}mph`] = {
      indicated: speed,
      actual: actualSpeed,
      error: error,
      errorPercentage: errorPct,
      correction: `${speed} mph indicated = ${actualSpeed.toFixed(1)} mph actual`
    };
  });

  return {
    ratio: ratio,
    summary: ratio > 1
      ? 'Speedometer will read SLOWER than actual speed'
      : ratio < 1
        ? 'Speedometer will read FASTER than actual speed'
        : 'No speedometer error',
    errors
  };
}

/**
 * Calculate drivetrain impact
 */
function calculateDrivetrainImpact(current, newTire, drivetrain) {
  const {
    axleGearRatio,
    transmissionTopGear = 1.0,
    transferCaseRatio = 1.0,
    transferCaseLowRatio = 2.5,
    firstGearRatio = 3.5  // Default first gear ratio for crawl calculations
  } = drivetrain;

  // Effective gear ratio change
  // New Effective Ratio = Original Ratio × (Original Diameter / New Diameter)
  const originalEffectiveRatio = axleGearRatio;
  const newEffectiveRatio = axleGearRatio * (current.diameter / newTire.diameter);
  const effectiveRatioChange = newEffectiveRatio - originalEffectiveRatio;
  const effectiveRatioChangePct = (effectiveRatioChange / originalEffectiveRatio) * 100;

  // RPM change at highway speed (65 mph)
  const testSpeed = 65; // mph
  const originalRPM = calculateEngineRPM(current.diameter, axleGearRatio, transmissionTopGear, testSpeed);
  const newRPM = calculateEngineRPM(newTire.diameter, axleGearRatio, transmissionTopGear, testSpeed);
  const rpmChange = newRPM - originalRPM;
  const rpmChangePct = (rpmChange / originalRPM) * 100;

  // Crawl ratio impact (4WD low range)
  // CRITICAL: Use FIRST gear ratio, not top gear!
  // Crawl Ratio = Axle × Transfer Case Low × First Gear
  // NOTE: Crawl ratio is ONLY about gears, NOT tire diameter
  // Larger tires make you move faster, but the ratio number stays the same
  const originalCrawlRatio = axleGearRatio * transferCaseLowRatio * firstGearRatio;
  const newCrawlRatio = axleGearRatio * transferCaseLowRatio * firstGearRatio; // Same! Only gears matter
  const crawlRatioChange = 0; // No change when gears stay the same
  const crawlRatioChangePct = 0;

  // Crawl SPEED impact (the real issue for rock crawlers)
  // Speed at idle (1000 RPM) in 4WD low, first gear
  // Formula: MPH = (RPM × Tire_Diameter) / (Gear_Ratio × Trans_Low × First_Gear × 336)
  const testRPM = 1000;
  const originalCrawlSpeed = (testRPM * current.diameter) / (axleGearRatio * transferCaseLowRatio * firstGearRatio * 336);
  const newCrawlSpeed = (testRPM * newTire.diameter) / (axleGearRatio * transferCaseLowRatio * firstGearRatio * 336);
  const crawlSpeedChange = newCrawlSpeed - originalCrawlSpeed;
  const crawlSpeedChangePct = (crawlSpeedChange / originalCrawlSpeed) * 100;

  return {
    effectiveGearRatio: {
      original: originalEffectiveRatio,
      new: newEffectiveRatio,
      change: effectiveRatioChange,
      changePercentage: effectiveRatioChangePct,
      summary: effectiveRatioChangePct < -5
        ? 'Effective gearing is LOWER (taller) - reduced acceleration, lower RPM'
        : effectiveRatioChangePct > 5
          ? 'Effective gearing is HIGHER (shorter) - improved acceleration, higher RPM'
          : 'Minimal effective gear ratio change'
    },
    rpm: {
      original: originalRPM,
      new: newRPM,
      change: rpmChange,
      changePercentage: rpmChangePct,
      testSpeed: testSpeed,
      summary: `${Math.abs(rpmChange).toFixed(0)} RPM ${rpmChange > 0 ? 'increase' : 'decrease'} at ${testSpeed} mph`
    },
    crawlRatio: {
      original: originalCrawlRatio,
      new: newCrawlRatio,
      change: crawlRatioChange,
      changePercentage: crawlRatioChangePct,
      summary: 'Crawl ratio unchanged (gear ratios determine crawl capability, not tire size)',
      crawlSpeed: {
        original: originalCrawlSpeed,
        new: newCrawlSpeed,
        change: crawlSpeedChange,
        changePercentage: crawlSpeedChangePct,
        testRPM: testRPM,
        summary: crawlSpeedChangePct > 5
          ? `Crawling ${Math.abs(crawlSpeedChangePct).toFixed(1)}% faster - reduced control on technical terrain`
          : crawlSpeedChangePct < -5
            ? `Crawling ${Math.abs(crawlSpeedChangePct).toFixed(1)}% slower - improved control`
            : 'Minimal impact on crawling speed'
      }
    }
  };
}

/**
 * Calculate engine RPM at given speed
 * RPM = (Speed × Gear Ratio × Trans Ratio × 336) / Tire Diameter
 */
function calculateEngineRPM(tireDiameter, axleRatio, transRatio, speedMPH) {
  return (speedMPH * axleRatio * transRatio * 336) / tireDiameter;
}

/**
 * Calculate clearance and fitment impact
 * NOTE: Lift recommendations are conservative. Many builds fit larger tires with
 * modifications like trimming, BMC, wheel offset changes, etc.
 */
function calculateClearanceImpact(differences, newTire, drivetrain = {}) {
  const diameterIncrease = differences.diameter.inches;
  const widthIncrease = differences.width.inches;

  // Calculate realistic lift requirement
  // Account for: ground clearance gain is HALF diameter increase (radius)
  // Most people can fit larger tires with trimming/mods before needing lift
  let estimatedLiftRequired = 0;
  let liftRecommendation = '';
  let modificationsNote = '';

  if (diameterIncrease <= 1.5) {
    // Small increase - usually no lift needed
    estimatedLiftRequired = 0;
    liftRecommendation = 'Stock suspension (trimming may be required)';
    modificationsNote = 'Minor fender liner trimming may help clearance';
  } else if (diameterIncrease <= 3) {
    // Medium increase - 1-2" lift or mods
    estimatedLiftRequired = 1;
    liftRecommendation = '1-2" lift recommended, or extensive trimming';
    modificationsNote = 'Many fit with no lift using: fender trimming, pinch weld modification, wheel spacers/offset';
  } else if (diameterIncrease <= 4.5) {
    // Large increase - 2-3" lift
    estimatedLiftRequired = 2;
    liftRecommendation = '2-3" lift recommended';
    modificationsNote = 'Fender trimming, BMC (body mount chop), and wheel offset changes typically required';
  } else if (diameterIncrease <= 6) {
    // Very large increase - 3-4" lift
    estimatedLiftRequired = 4;
    liftRecommendation = '3-4" lift recommended';
    modificationsNote = 'Significant modifications needed: extensive cutting, BMC, custom suspension, possible control arm issues';
  } else {
    // Extreme increase - 4"+ lift
    estimatedLiftRequired = 5;
    liftRecommendation = '4-6" lift required';
    modificationsNote = 'Major build: long-travel suspension, custom control arms, extensive fabrication';
  }

  // Fender clearance concerns
  const fenderClearanceConcern = widthIncrease > 1 || diameterIncrease > 2;

  // Backspacing/offset change needed
  const offsetChangeNeeded = widthIncrease > 1.5;

  // Enhanced clearance probability analysis (Part 3: Engineering Expansion)
  let probabilityAnalysis = null;
  if (drivetrain.liftHeight !== undefined || drivetrain.suspensionType || drivetrain.vehicleType) {
    const suspensionType = drivetrain.suspensionType || getVehicleSuspensionType(drivetrain.vehicleType || 'generic');

    probabilityAnalysis = calculateClearanceProbability({
      suspensionType,
      liftHeight: drivetrain.liftHeight || 0,
      diameterIncrease: Math.abs(diameterIncrease),
      widthIncrease: Math.abs(widthIncrease),
      newDiameter: newTire.diameter,
      newWidth: newTire.widthInches,
      vehicleType: drivetrain.vehicleType || 'generic'
    });
  }

  return {
    groundClearanceGain: differences.groundClearance.inches,
    estimatedLiftRequired: estimatedLiftRequired,
    liftRecommendation: liftRecommendation,
    modificationsNote: modificationsNote,
    fenderClearance: {
      concern: fenderClearanceConcern,
      message: fenderClearanceConcern
        ? 'Fender trimming or body mount chop likely required'
        : 'Should clear fenders with minor or no trimming'
    },
    wheelOffset: {
      changeNeeded: offsetChangeNeeded,
      message: offsetChangeNeeded
        ? 'Consider wheels with less backspacing or wheel spacers to prevent rubbing'
        : 'Stock wheel offset should work'
    },
    bumpstopModification: diameterIncrease > 1.5
      ? 'Bump stop modification likely required to prevent tire contact at full compression'
      : null,
    probabilityAnalysis // Enhanced clearance probability (Part 3)
  };
}

/**
 * Format tire for display
 */
function formatDisplay(tire) {
  // Use the original raw string if available (preserves user's exact input format)
  if (tire.raw) {
    return tire.raw;
  }

  // Fallback formatting if raw not available
  if (tire.format === 'Flotation') {
    return `${tire.diameter.toFixed(1)}x${(tire.width / 25.4).toFixed(2)}R${tire.wheelDiameter}`;
  }
  const prefix = tire.isLT ? 'LT' : '';
  return `${prefix}${Math.round(tire.width)}/${tire.aspectRatio}R${tire.wheelDiameter}`;
}

/**
 * Estimate tire weight based on size and type
 * Uses real-world tire specifications from major manufacturers
 * Calibrated against: BFG KO2, Nitto Ridge Grappler, Goodyear Wrangler, General Grabber, Pro Comp
 * Average accuracy: ~92-95% (within ±8 lbs for most tires)
 * @param {Object} tire - Tire metrics
 * @returns {number} Estimated weight in pounds
 */
function estimateTireWeight(tire) {
  const diameter = tire.diameter;
  const widthInches = tire.widthInches;
  const isLT = tire.isLT;

  // Base weight from diameter (calibrated from real tire data)
  // P-metric: ~1.5 lbs per inch of diameter
  // LT/Flotation: ~1.85 lbs per inch of diameter
  let baseWeight = diameter * (isLT ? 1.85 : 1.5);

  // Width contribution (wider tires are significantly heavier)
  // Flotation tires have wide aggressive treads
  let widthMultiplier = isLT ? 0.7 : 0.5;
  if (tire.format === 'Flotation' && widthInches > 12) {
    widthMultiplier = 1.1; // Very wide flotation tires are much heavier
  } else if (tire.format === 'Flotation') {
    widthMultiplier = 0.85;
  }

  const widthContribution = Math.max(0, (widthInches - 9) * widthMultiplier);
  let estimatedWeight = baseWeight + widthContribution;

  // Apply corrections based on aspect ratio
  if (tire.aspectRatio < 65) {
    estimatedWeight *= 0.92;
  } else if (tire.aspectRatio > 75) {
    estimatedWeight *= 1.08;
  }

  // Additional correction for very wide LT metric tires (315mm+)
  if (isLT && tire.width >= 315 && tire.format !== 'Flotation') {
    estimatedWeight *= 1.10;
  }

  // Round to nearest pound
  return Math.round(estimatedWeight);
}

/**
 * Calculate weight impact (unsprung mass analysis)
 * @param {Object} tireSpecs - Tire weight specifications
 * @param {boolean} isEstimate - Whether weights are estimated vs user-provided
 * @param {string} intendedUse - Intended use case for context-aware recommendations
 * @returns {Object} Weight impact analysis
 */
function calculateWeightImpact(tireSpecs, isEstimate = false, intendedUse = 'weekend_trail') {
  const currentWeight = parseFloat(tireSpecs.currentTireWeight);
  const newWeight = parseFloat(tireSpecs.newTireWeight);

  const weightDifference = newWeight - currentWeight;
  const weightDifferencePct = (weightDifference / currentWeight) * 100;

  // Unsprung weight = 4 tires + wheels (estimate wheels at 25 lbs each)
  const currentUnsprungTireWeight = currentWeight * 4;
  const newUnsprungTireWeight = newWeight * 4;
  const totalUnsprungWeightIncrease = weightDifference * 4;

  // Determine severity based on use case
  // Rock crawling: weight is less critical (durability more important)
  // Daily driver: weight matters more (fuel economy, ride quality)
  let severityThresholds = { high: 30, medium: 15 }; // default

  if (intendedUse === 'daily_driver') {
    severityThresholds = { high: 25, medium: 12 }; // more sensitive
  } else if (intendedUse === 'rock_crawling') {
    severityThresholds = { high: 40, medium: 20 }; // less sensitive
  }

  const severity = Math.abs(weightDifferencePct) > severityThresholds.high ? 'high'
    : Math.abs(weightDifferencePct) > severityThresholds.medium ? 'medium'
      : 'low';

  return {
    isEstimate,
    current: {
      perTire: currentWeight,
      total: currentUnsprungTireWeight
    },
    new: {
      perTire: newWeight,
      total: newUnsprungTireWeight
    },
    difference: {
      perTire: weightDifference,
      perTirePct: weightDifferencePct,
      total: totalUnsprungWeightIncrease,
      totalPct: weightDifferencePct
    },
    impact: {
      severity,
      acceleration: weightDifference > 0
        ? `${Math.abs(weightDifference).toFixed(1)} lbs/tire increase reduces acceleration`
        : `${Math.abs(weightDifference).toFixed(1)} lbs/tire decrease improves acceleration`,
      suspension: totalUnsprungWeightIncrease > 40
        ? 'Significant unsprung weight increase - suspension may feel harsh, consider upgrading shocks'
        : totalUnsprungWeightIncrease > 20
          ? 'Moderate unsprung weight increase - ride quality slightly affected'
          : 'Minimal suspension impact',
      braking: totalUnsprungWeightIncrease > 40
        ? 'Braking distances will increase - consider brake upgrades'
        : totalUnsprungWeightIncrease > 0
          ? 'Slightly longer braking distances'
          : 'Improved braking response',
      handling: totalUnsprungWeightIncrease > 40
        ? 'Reduced suspension compliance - slower rebound over rough terrain'
        : 'Minimal handling impact'
    },
    recommendations: generateWeightRecommendations(weightDifference, totalUnsprungWeightIncrease, intendedUse)
  };
}

/**
 * Generate recommendations based on weight change and intended use
 * @param {number} perTireChange - Weight change per tire in pounds
 * @param {number} totalChange - Total weight change for 4 tires
 * @param {string} intendedUse - Intended use case
 */
function generateWeightRecommendations(perTireChange, totalChange, intendedUse = 'weekend_trail') {
  const recommendations = [];

  // Shock recommendations
  if (totalChange > 60) {
    recommendations.push('Consider upgrading to heavy-duty shocks (Bilstein, Fox, King) to handle increased unsprung weight');
    if (intendedUse === 'rock_crawling') {
      recommendations.push('Heavy shocks essential for rock crawling - better control on technical terrain');
    }
    recommendations.push('Upgraded brakes strongly recommended for safety');
  } else if (totalChange > 40) {
    recommendations.push('Performance shocks recommended for better control');
    recommendations.push('Monitor brake pad wear - may need upgrades');
  } else if (totalChange > 20) {
    if (intendedUse === 'daily_driver') {
      recommendations.push('Stock shocks may wear faster - consider upgrade for better ride quality');
    } else {
      recommendations.push('Stock shocks should work but may wear faster');
    }
  }

  // Fuel economy (more critical for daily drivers and overlanding)
  if (perTireChange > 20) {
    if (intendedUse === 'daily_driver') {
      recommendations.push('Expect 1.0-2.0 MPG fuel economy decrease - significant for daily commuting');
    } else if (intendedUse === 'overlanding') {
      recommendations.push('Expect 0.5-1.5 MPG decrease - factor into fuel range planning for remote trips');
    } else {
      recommendations.push('Expect 0.5-1.0 MPG fuel economy decrease from weight alone');
    }
  }

  // Heavy use warnings
  if (totalChange > 80) {
    recommendations.push('Wheel bearing service intervals should be shortened');
    recommendations.push('Ball joint and tie rod wear will accelerate');
  }

  // Use-case specific advice
  if (intendedUse === 'rock_crawling' && perTireChange > 0) {
    recommendations.push('Heavier tires often more durable for rock crawling - accept weight trade-off for sidewall strength');
  } else if (intendedUse === 'sand_desert' && perTireChange > 10) {
    recommendations.push('Weight increase reduces flotation in sand - may need to air down more (12-15 PSI)');
  } else if (intendedUse === 'daily_driver' && totalChange > 30) {
    recommendations.push('Consider if weight penalty worth it for daily use - affects acceleration, braking, and comfort');
  }

  // Positive weight reduction
  if (perTireChange < -5) {
    if (intendedUse === 'daily_driver') {
      recommendations.push('Weight reduction improves fuel economy and acceleration - excellent for daily driving');
    } else if (intendedUse === 'overlanding') {
      recommendations.push('Lighter tires leave more payload capacity for gear and equipment');
    }
  }

  return recommendations.length > 0 ? recommendations : ['Weight difference is minimal - no special modifications needed'];
}

/**
 * Load index to pounds capacity lookup
 */
const LOAD_INDEX_TABLE = {
  70: 739, 71: 761, 72: 783, 73: 805, 74: 827, 75: 853, 76: 882, 77: 908, 78: 937, 79: 963,
  80: 992, 81: 1019, 82: 1047, 83: 1074, 84: 1102, 85: 1135, 86: 1168, 87: 1201, 88: 1235, 89: 1279,
  90: 1323, 91: 1356, 92: 1389, 93: 1433, 94: 1477, 95: 1521, 96: 1565, 97: 1609, 98: 1653, 99: 1709,
  100: 1764, 101: 1819, 102: 1874, 103: 1929, 104: 1984, 105: 2039, 106: 2094, 107: 2149, 108: 2205, 109: 2271,
  110: 2337, 111: 2403, 112: 2469, 113: 2535, 114: 2601, 115: 2679, 116: 2756, 117: 2833, 118: 2910, 119: 2998,
  120: 3086, 121: 3197, 122: 3307, 123: 3417, 124: 3527, 125: 3638, 126: 3748, 127: 3858, 128: 3968, 129: 4079,
  130: 4189
};

/**
 * Calculate load capacity analysis
 * @param {Object} tireSpecs - Load index specifications
 * @param {string} intendedUse - Intended use case
 * @returns {Object} Load capacity analysis
 */
function calculateLoadCapacity(tireSpecs, intendedUse = 'weekend_trail') {
  const currentIndex = parseInt(tireSpecs.currentTireLoadIndex);
  const newIndex = parseInt(tireSpecs.newTireLoadIndex);

  const currentCapacity = LOAD_INDEX_TABLE[currentIndex] || 0;
  const newCapacity = LOAD_INDEX_TABLE[newIndex] || 0;

  if (!currentCapacity || !newCapacity) {
    return {
      error: 'Invalid load index values. Must be between 70 and 130.'
    };
  }

  const capacityChange = newCapacity - currentCapacity;
  const capacityChangePct = (capacityChange / currentCapacity) * 100;

  // Total vehicle capacity (4 tires)
  const currentTotalCapacity = currentCapacity * 4;
  const newTotalCapacity = newCapacity * 4;
  const totalCapacityChange = capacityChange * 4;

  // Severity assessment varies by use case
  let severity;
  if (intendedUse === 'overlanding' || intendedUse === 'rock_crawling') {
    // More strict for heavy use
    severity = capacityChange < -300 ? 'critical'
      : capacityChange < -150 ? 'important'
        : capacityChange < 0 ? 'advisory'
          : 'positive';
  } else {
    // Standard thresholds
    severity = capacityChange < -200 ? 'critical'
      : capacityChange < -100 ? 'important'
        : capacityChange < 0 ? 'advisory'
          : 'positive';
  }

  return {
    current: {
      loadIndex: currentIndex,
      capacityPerTire: currentCapacity,
      totalCapacity: currentTotalCapacity
    },
    new: {
      loadIndex: newIndex,
      capacityPerTire: newCapacity,
      totalCapacity: newTotalCapacity
    },
    difference: {
      loadIndexChange: newIndex - currentIndex,
      capacityPerTire: capacityChange,
      capacityPerTirePct: capacityChangePct,
      totalCapacity: totalCapacityChange
    },
    assessment: {
      severity,
      suitability: assessLoadSuitability(newTotalCapacity, intendedUse),
      warning: capacityChange < -200
        ? 'CRITICAL: Significant load capacity reduction - verify tire rating supports vehicle weight'
        : capacityChange < -100
          ? 'IMPORTANT: Reduced load capacity - not recommended for heavy loads or overlanding'
          : capacityChange < 0
            ? 'Advisory: Slightly reduced load capacity'
            : capacityChange > 200
              ? 'Excellent: Increased load capacity ideal for overlanding and heavy gear'
              : 'Load capacity maintained or improved'
    },
    recommendations: generateLoadRecommendations(capacityChange, newTotalCapacity, newIndex, intendedUse)
  };
}

/**
 * Assess load suitability for different use cases
 * @param {number} totalCapacity - Total capacity across 4 tires in pounds
 * @param {string} intendedUse - Intended use case
 */
function assessLoadSuitability(totalCapacity, intendedUse = 'weekend_trail') {
  const assessments = [];

  // Typical vehicle weights (loaded):
  // Tacoma/4Runner loaded: 5500-7000 lbs
  // Jeep Wrangler loaded: 4500-6000 lbs
  // Full-size truck loaded: 7000-9000 lbs

  if (intendedUse === 'overlanding') {
    // Higher capacity needs for overlanding (gear, water, recovery equipment, armor)
    if (totalCapacity >= 12000) {
      assessments.push('Excellent for heavily loaded overland rigs with RTT, gear, armor, and extended fuel/water');
    } else if (totalCapacity >= 10000) {
      assessments.push('Good for loaded overlanding with moderate gear - watch total weight with full equipment');
    } else if (totalCapacity >= 8000) {
      assessments.push('Marginal for overlanding - limit armor and heavy modifications');
    } else {
      assessments.push('Insufficient capacity for serious overlanding - consider higher load index tires');
    }
  } else if (intendedUse === 'rock_crawling') {
    // High capacity important for protection (armor, sliders, bumpers)
    if (totalCapacity >= 11000) {
      assessments.push('Excellent capacity for armor, sliders, heavy bumpers, and recovery gear');
    } else if (totalCapacity >= 9000) {
      assessments.push('Good capacity for typical rock crawling armor and protection');
    } else if (totalCapacity >= 7500) {
      assessments.push('Adequate for light armor - limit heavy steel bumpers');
    } else {
      assessments.push('Low capacity - verify total vehicle weight with armor stays well under rating');
    }
  } else if (intendedUse === 'daily_driver') {
    // Lower capacity acceptable for daily use
    if (totalCapacity >= 9000) {
      assessments.push('Excellent capacity - more than adequate for daily driving and occasional trips');
    } else if (totalCapacity >= 7500) {
      assessments.push('Good capacity for daily driving and light gear');
    } else if (totalCapacity >= 6000) {
      assessments.push('Adequate for daily driver without heavy modifications');
    } else {
      assessments.push('Limited capacity - avoid heavy loads or verify vehicle weight');
    }
  } else {
    // Default for weekend trail, sand/desert
    if (totalCapacity >= 12000) {
      assessments.push('Excellent for heavily loaded overland rigs with RTT, gear, armor');
    } else if (totalCapacity >= 10000) {
      assessments.push('Good for loaded overlanding with moderate gear');
    } else if (totalCapacity >= 8000) {
      assessments.push('Adequate for daily driving and light trail use');
    } else {
      assessments.push('Limited capacity - verify vehicle weight is well under tire rating');
    }
  }

  return assessments;
}

/**
 * Generate load capacity recommendations based on use case
 * @param {number} capacityChange - Change in capacity per tire
 * @param {number} totalCapacity - Total capacity across 4 tires
 * @param {number} loadIndex - New tire load index
 * @param {string} intendedUse - Intended use case
 */
function generateLoadRecommendations(capacityChange, totalCapacity, loadIndex, intendedUse = 'weekend_trail') {
  const recommendations = [];

  // Critical warnings
  if (capacityChange < -200) {
    recommendations.push('CRITICAL: Weigh your vehicle fully loaded to ensure tires are rated appropriately');
    recommendations.push('Consider higher load index tires (Load Range E) for safety');

    if (intendedUse === 'overlanding') {
      recommendations.push('Capacity reduction dangerous for overlanding - fully loaded weight can easily exceed rating');
    }
  } else if (capacityChange < -100) {
    recommendations.push('Verify vehicle weight is within safe limits of new tire capacity');
    if (intendedUse === 'overlanding' || intendedUse === 'rock_crawling') {
      recommendations.push('Not recommended for heavy overland loads or armor with this capacity reduction');
    } else {
      recommendations.push('Avoid heavy loads with this tire');
    }
  }

  // Use-case specific recommendations
  if (intendedUse === 'overlanding') {
    if (loadIndex < 115) {
      recommendations.push('Load index <115 marginal for overlanding - especially with RTT, water, gear, and armor');
    }
    if (totalCapacity < 10000) {
      recommendations.push('Total capacity <10000 lbs limits overlanding capability - weigh rig fully loaded');
    }
    if (loadIndex >= 121) {
      recommendations.push('Load Range E excellent for overlanding - handles heavy gear, water, recovery equipment');
    }
  } else if (intendedUse === 'rock_crawling') {
    if (loadIndex < 113) {
      recommendations.push('Load index <113 not ideal for rock crawling with armor, sliders, and heavy bumpers');
    }
    if (totalCapacity < 9000) {
      recommendations.push('Total capacity <9000 lbs limits armor options - consider higher rating for protection');
    }
    if (loadIndex >= 121) {
      recommendations.push('Load Range E ideal for rock crawling - supports full armor package');
    }
  } else if (intendedUse === 'daily_driver') {
    if (loadIndex < 110) {
      recommendations.push('Load index <110 adequate for daily driving but limit roof racks and heavy cargo');
    }
    if (capacityChange > 200) {
      recommendations.push('Capacity increase provides safety margin - good for occasional hauling or trips');
    }
  } else {
    // Default recommendations
    if (loadIndex < 110) {
      recommendations.push('Load index <110 not recommended for armor, roof racks, or expedition builds');
    }
    if (totalCapacity < 9000) {
      recommendations.push('Total capacity <9000 lbs - limit heavy modifications and cargo weight');
    }
  }

  // Positive capacity increase
  if (capacityChange > 300) {
    if (intendedUse === 'overlanding') {
      recommendations.push('Excellent capacity increase - perfect for fully loaded overland expeditions');
    } else if (intendedUse === 'rock_crawling') {
      recommendations.push('Excellent capacity increase - supports comprehensive armor and recovery gear');
    } else {
      recommendations.push('Excellent capacity increase - well-suited for overlanding and heavy builds');
    }
  }

  // High load index praise
  if (loadIndex >= 121 && intendedUse !== 'daily_driver') {
    recommendations.push('Load Range E rating - excellent for serious off-road and expedition use');
  }

  return recommendations.length > 0 ? recommendations : ['Load capacity appropriate for typical use'];
}
